import React, { CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect, MapStateToProps, useDispatch, useSelector } from "react-redux";
import { Label, setResponsiveSearchVisible, UnstyledButton, setDisplayHeaderSearch } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import "./SpintrHeaderMenu.scss";
import { localize } from "src/l10n";
import { Link, RouteComponentProps, useLocation, withRouter } from "react-router-dom";
import { SpintrTypes } from "src/typings";
import ContentWithUnreadIndicator from "src/ui/components/UnreadIndicator/ContentWithUnreadIndicator";
import { fetchOperatingInfo } from "src/operating-info/actions";
import getLightOrDarkColorBasedOnColor from "src/utils/getLightOrDarkColorBasedOnColor";
import { Style } from "src/ui/helpers";
import SpintrHeaderMenuSearch from "./SpintrHeaderMenuSearch";
import useKeyboardShortcut from "src/keyboard-shortcuts/useKeyboardShortcut";
import { fetchSystemStatusOngoingCount } from "src/system-status/actions";
import { MainHeaderSearch } from "src/components";
import { isHubAdministrator } from "src/redux";

interface IOwnProps {
    responsive?: boolean;
}

interface IStateProps {
    modernSearchExperience?: boolean;
    operatingInfoEnabled?: boolean;
    operatingInfoCategories?: any;
    unreadCountGroupConversations?: number;
    ongoingCountSystemStatuses?: number;
    useColorHeader?: boolean;
    headerColor?: string;
    academyEnabled?: boolean;
    groupsEnabled?: boolean;
    plannerEnabled: boolean;
    disablePersonChat?: boolean;
    academyAppName?: string;
    systemStatusEnabled?: boolean;
    instanceName?: string;
    displayHeaderSearch?: boolean;
    isSmallViewMode?: boolean;

    qualityHub?: undefined | {
        enabled: boolean;
        isQualityHubUser?: boolean;
        isHubAdministrator?: boolean;
    },
}

type IProps = IOwnProps & IStateProps & RouteComponentProps

interface IHeaderMenuItem {
    key: string;
    icon: string;
    iconType?: "bold" | "outline" | "custom" | undefined;
    text?: string;
    title: string;
    routes: string[];
    exact?: boolean;
    isSearch?: boolean;
    enabled: boolean;
    unreadCount?: number;
    action?: () => void;
}

interface IState {
    items: IHeaderMenuItem[];
    activeIndex: number;
    isMeasuring: boolean;
    itemWidths: number[];
    isMac: boolean;
    responsiveIndicatorStyle: CSSProperties;
}

const getQualityHubMenuItem = (props: IProps): IHeaderMenuItem => {
    const routes: string[] = [];

    routes.push("/quality-hub/home");
    // if (props.qualityHub?.isQualityHubUser) {}

    if (props.qualityHub?.isHubAdministrator) {
        routes.push("/quality-hub/admin");
    }

    if (routes.length > 0) {
        routes.push("/quality-hub/");
    }

    return {
        key: "qualityHub",
        icon: "task-square",
        text: localize("appQualityHub"),
        title: localize("appQualityHub"),
        routes,
        enabled: (props.qualityHub.enabled ?? false) && routes.length > 0,
    };
};

const SpintrHeaderMenu = (props: IProps) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const wrapperRef = useRef<HTMLDivElement>(undefined);
    const overlayId = useSelector((state: Spintr.AppState) => state.ui.overlayId);

    const getItems = () => {
        const items: IHeaderMenuItem[] = [{
            key: "home",
            icon: "home-2",
            text: props.instanceName,
            title: props.instanceName,
            routes: ["/"],
            enabled: true,
            exact: true
        }, {
            key: "groups",
            icon: "profile-2user",
            text: localize("COLLABORATION"),
            title: localize("COLLABORATION"),
            routes: ["/groups", "/my-groups"],
            unreadCount: props.unreadCountGroupConversations,
            enabled: !props.disablePersonChat || props.groupsEnabled,
        }, {
            key: "academy",
            icon: "teacher",
            text: localize(props.academyAppName, true),
            title: localize(props.academyAppName, true),
            routes: ["/learning"],
            enabled: props.academyEnabled
        }, {
            key: "operatinginfo",
            icon: "cardiogram",
            iconType: "custom",
            text: localize("Driftinformation"),
            title: localize("Driftinformation"),
            routes: ["/operatinginfo"],
            enabled: props.operatingInfoEnabled && !props.systemStatusEnabled,
            unreadCount: props.operatingInfoCategories.length > 0 ? props.operatingInfoCategories[0].items.length : 0
        }, {
            key: "systemstatus",
            icon: "cardiogram",
            iconType: "custom",
            text: localize("appSystemStatus"),
            title: localize("appSystemStatus"),
            routes: ["/operations"],
            enabled: props.systemStatusEnabled,
            unreadCount: props.ongoingCountSystemStatuses,
        }, {
            key: "planner",
            icon: "calendar-1",
            text: localize("appPlanner"),
            title: localize("appPlanner"),
            routes: ["/timeline"],
            enabled: props.plannerEnabled,
        }, 
        getQualityHubMenuItem(props),
        {
            key: "search",
            icon: "search-normal-1",
            text: localize("Sok"),
            title: localize("Sok"),
            routes: ["/search"],
            action: () => {
                dispatch(setResponsiveSearchVisible(true));
            },
            enabled: props.responsive
        }, {
            key: "search",
            icon: props.modernSearchExperience ? "ai-search" : "search-normal-1",
            iconType: props.modernSearchExperience ? "custom" : undefined,
            title: localize("Sok"),
            routes: ["/search"],
            isSearch: true,
            enabled: !props.responsive
        }];
    
        return items.filter(x => x.enabled);
    };

    const [state, setState] = useState<IState>(() => {
        return {
            items: getItems(),
            activeIndex: 0,
            isMeasuring: true,
            itemWidths: [],
            isMac: window.navigator.platform.indexOf("Mac") > -1,
            responsiveIndicatorStyle: {
                left: 0,
                width: 0,
            },
        }
    });

    const focusSearchField = useCallback(() => {
        const assignFocus = () => {
            document.querySelector<HTMLInputElement>(".query-input")?.focus();
        };

        if (!props.modernSearchExperience) {
            return;
        }

        setTimeout(assignFocus, 10);
    }, []);

    useKeyboardShortcut({
        key: "k",
        holdCtrlKey: !state.isMac,
        holdMetaKey: state.isMac,
        callback: () => {
            if (!props.modernSearchExperience) {
                dispatch(setDisplayHeaderSearch(true));

                setState((s) => ({
                    ...s,
                    activeIndex: state.items.length - 1
                }));
            }

            focusSearchField();
        }
    });

    useEffect(() => {
        setState((s) => ({
            ...s,
            items: getItems(),
        }));
    }, [props.operatingInfoCategories, props.unreadCountGroupConversations, props.ongoingCountSystemStatuses]);

    useEffect(() => {
        let activeIndex = -1;

        for (let i = 0; i < state.items.length; i++) {
            if (state.items[i].routes.length === 0) {
                continue;
            }

            if (state.items[i].exact &&
                state.items[i].routes.some(x => location.pathname === x)) {
                activeIndex = i;
            }

            if (!state.items[i].exact &&
                state.items[i].routes.some(x => location.pathname.startsWith(x))) {
                activeIndex = i;
            }
        }

        setState((s) => ({
            ...s,
            activeIndex
        }));
    }, [location, state.items]);

    useEffect(() => {
        const intervalRef = setInterval(() => {
            if (!wrapperRef.current) {
                return;
            }
    
            const itemWidths = Array.from(wrapperRef.current.children).map(x => x.clientWidth);

            if (itemWidths.every(x => x === 0 || (x === 44 && !props.isSmallViewMode && props.modernSearchExperience))) {
                return;
            }

            clearInterval(intervalRef);
    
            setState((s) => ({
                ...s,
                isMeasuring: false,
                itemWidths,
            }));
        }, 500);

        return () => clearInterval(intervalRef);
    }, [wrapperRef.current, props.isSmallViewMode, props.modernSearchExperience]);

    useLayoutEffect(() => {
        if (state.isMeasuring || state.activeIndex < 0 || !wrapperRef.current) {
            return;
        }

        const activeElement = Array.from(wrapperRef.current.children)[state.activeIndex] as HTMLElement | undefined;
        if (!activeElement) {
            return;
        }

        const nextStyle: CSSProperties = {
            left: activeElement.offsetLeft,
            width: activeElement.offsetWidth,
        };

        setState((s) => {
            if (
                s.responsiveIndicatorStyle.left === nextStyle.left
                && s.responsiveIndicatorStyle.width === nextStyle.width
            ) {
                return s;
            }

            return {
                ...s,
                responsiveIndicatorStyle: nextStyle,
            };
        });
    }, [state.activeIndex, state.isMeasuring, state.items]);

    useEffect(() => {
        if (props.operatingInfoEnabled) {
            dispatch(fetchOperatingInfo({
                showDeleted: false,
                typesConfig: "operatingInfoSidebar",
            }));
        }

        if (props.systemStatusEnabled) {
            dispatch(fetchSystemStatusOngoingCount());
        }
    }, []);

    const indicatorStyle = useMemo(() => {
        if (state.activeIndex === -1) {
            return {
                left: 0,
                width: 0
            };
        }

        if (props.modernSearchExperience) {
            const searchIndex = state.items.findIndex(i => i.isSearch);
            if (searchIndex !== -1) {
                if (state.activeIndex === searchIndex) {
                    return {
                        left: 0,
                        width: 0,
                        display: "none",
                    };
                }
            }
        }

        return state.responsiveIndicatorStyle;
    }, [props.modernSearchExperience, state.activeIndex, state.items, state.responsiveIndicatorStyle, state.isMeasuring]);

    const headerColor = useMemo(() => {
        return props.useColorHeader ? (props.headerColor ?? "#FFFFFF") : "#FFFFFF";
    }, []);

    const inactiveTextColor = useMemo(() => {
        if (headerColor === "#FFFFFF") {
            return Style.getHexFromSpintrColor("primaryContent");
        }

        return getLightOrDarkColorBasedOnColor(headerColor,
            Style.getHexFromSpintrColor("light-grey"),
            Style.getHexFromSpintrColor("primaryContent"));
    }, []);

    const activeTextColor = useMemo(() => {
        if (headerColor === "#FFFFFF") {
            return Style.getHexFromSpintrColor("primaryContent");
        }

        return getLightOrDarkColorBasedOnColor(headerColor,
            Style.getHexFromSpintrColor("light-grey"),
            Style.getHexFromSpintrColor("primaryContent"));
    }, []);

    const classNames = useMemo(() => {
        const result = ["SpintrHeaderMenu"];

        if (state.isMeasuring) {
            result.push("is-measuring");
        }

        return result.join(" ");
    }, [state.isMeasuring]);

    return (
        <div ref={wrapperRef} className={classNames}>
            {state.items.map((item, index) => {

                const classes = ["menu-item"];
                const isActive = index === state.activeIndex || (item.isSearch && props.modernSearchExperience && overlayId === "search");

                if (isActive) {
                    classes.push("active");
                }

                if (item.isSearch) {
                    classes.push("search");
                }

                if (props.modernSearchExperience && item.isSearch) {
                    classes.push("modern-search");
                }

                return (
                    <SpintrHeaderMenuLink
                        isActive={item.isSearch && props.modernSearchExperience}
                        key={index}
                        item={item}
                        classes={classes.join(" ")}
                        onClick={() => {
                            if (item.action) {
                                return item.action();
                            }

                            if (item.isSearch) {
                                dispatch(setDisplayHeaderSearch(true));
                                focusSearchField();
                            }

                            setState((s) => ({
                                ...s,
                                activeIndex: item.isSearch && props.modernSearchExperience ? s.activeIndex : index
                            }));
                        }}
                    >
                        {!(item.isSearch && props.modernSearchExperience) && (
                            <ContentWithUnreadIndicator count={item.unreadCount} margin={11} secondaryPlacement>
                                <Visage2Icon
                                    icon={item.icon}
                                    type={item.iconType}
                                    useStrokeForColor={item.icon === "cardiogram"}
                                    hexColor={isActive ? activeTextColor : inactiveTextColor}
                                />
                            </ContentWithUnreadIndicator>
                        )}
                        {(isActive || state.isMeasuring || props.responsive || (item.isSearch && props.modernSearchExperience)) && (
                            <div className="content">
                                {item.text && <Label weight="medium" style={{color: isActive ? activeTextColor : inactiveTextColor}}>{item.text}</Label>}
                                {item.isSearch && !props.modernSearchExperience && <SpintrHeaderMenuSearch />}
                                {item.isSearch && props.modernSearchExperience && <MainHeaderSearch />}
                            </div>
                        )}
                    </SpintrHeaderMenuLink>
                )
            })}
            {!state.isMeasuring && state.activeIndex > -1 && (
                <div
                    className="indicator header-gradient-border"
                    style={indicatorStyle}
                >
                    <div className="gradient" />
                </div>
            )}
        </div>
    );
}

interface SpintrHeaderMenuLinkProps {
    item: IHeaderMenuItem;
    classes: string;
    onClick: () => void;
    children?: any;
    isActive?: boolean;
}

const SpintrHeaderMenuLink = (props: SpintrHeaderMenuLinkProps) => {
    if (props.item.isSearch && props.isActive) {
        return (
            <div className={props.classes}>
                {props.children}
            </div>
        );
    }

    if (props.item.action || props.item.isSearch || props.item.routes.length === 0) {
        return (
            <UnstyledButton
                className={props.classes}
                title={props.item.title}
                onClick={props.onClick}>
                {props.children}
            </UnstyledButton>
        )
    }

    return (
        <Link
            to={props.item.routes[0]}
            className={props.classes}
            title={props.item.title}
            onClick={props.onClick}>
            {props.children}
        </Link>
    )
}

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, Spintr.AppState>
    = (state) => ({
        plannerEnabled: state.app.items.some(
            (app) => app.enabled && app.id === SpintrTypes.SpintrApp.Planner,
        ),
        groupsEnabled: state.profile.active.rights.hasAccessToGroups,
        disablePersonChat: state.instance.get("disablePersonChat"),
        operatingInfoEnabled: !!state.instance.get("apps").find((a) => a.id === SpintrTypes.SpintrApp.OperatingInfo && a.enabled),
        operatingInfoCategories: state.operatingInfo.currentCategories,
        unreadCountGroupConversations: state.chat.conversations.hasFetched ?
        (state.chat.conversations.items || []).filter((c: any) => c.unread > 0).length :
        (state.chat.conversations.unreadCountGroupConversations + state.chat.conversations.unreadCountPersonConversations),
        ongoingCountSystemStatuses: state.systemStatus.ongoingCount,
        useColorHeader: state.instance.get("useColorHeader"),
        headerColor: state.instance.get("headerColor"),
        academyEnabled: state.app.items.some((app) => app.enabled && app.id === SpintrTypes.SpintrApp.Academy),
        academyAppName: state.instance.get("academyAppName"),
        systemStatusEnabled: state.app.items.some((app) => app.enabled && app.id === SpintrTypes.SpintrApp.SystemStatus),
        instanceName: state.instance.get("name"),
        modernSearchExperience: state.instance.get("modernSearchExperience") ?? false,
        displayHeaderSearch: state.ui.displayHeaderSearch,
        qualityHub: {
            enabled: state.app.items.some((app) => app.enabled && app.id === SpintrTypes.SpintrApp.QualityHub),
            isHubAdministrator: isHubAdministrator(state),
            isQualityHubUser: state.profile.active.qualityHubUser,
        },
        isSmallViewMode: state.ui.viewMode <= SpintrTypes.ViewMode.TabletLandscape,
    });

export default withRouter(connect(mapStateToProps)(SpintrHeaderMenu));
